// Photo gallery on MLS listing cards (SPEC-0033) — the slider inside
// webdelo.idx::partials.catalog-cards when a listing has more than one photo.
//
// Not the site's initSliders(): that one runs once on load and pairs the
// .card__slider elements with their arrows by position in the document. These
// cards also arrive later — "show more", the map list and popup, favorites — so
// every gallery mounts its own Swiper when it nears the viewport, and a
// MutationObserver picks up the ones inserted afterwards, with no hook needed
// wherever such HTML is inserted — and destroys the Swipers of the ones taken
// out (the map swaps its list wholesale on every move, favorites refill theirs):
// each instance keeps listeners on document. The arrows are not
// .card-button_prev/_next for the same reason initSliders() is avoided: the
// site's sliders pair those with sliders by position in the whole document.
//
// The page carries the cover and the number of photos only (§4.1): a card weighs
// the same with 2 photos or 100. The list is fetched from the URL in
// data-idx-gallery on the first hover, touch or focus — before the first flip —
// and the Swiper is virtual: only the shown slide and its neighbours are ever in
// the DOM, which also loads the shown photo and the one after it. A slide paged
// to before the list arrives shows the grey frame and fills in when it does.

import Swiper, {Navigation, Pagination, Virtual} from "swiper";

const GALLERY = '[data-idx-gallery]';

// Set on the gallery by mount() and kept by the map's snapshot of the list (it
// copies innerHTML): a gallery paged before the snapshot comes back showing some
// other photo, and this is how it finds its cover again.
const COVER = 'data-idx-gallery-cover';

// Cached per page: the same card may be rendered again by "show more" or the map.
const lists = new Map();

// Resolves to the list, or to null when it is worth asking again (offline, 5xx).
// A 4xx is final: the cached page outlived the listing (sold, hidden, re-imported
// under a new id), and the gallery folds down to its cover instead of offering
// empty frames and asking again on every hover.
function fetchList(src) {
    if (!lists.has(src)) {
        lists.set(src, fetch(src, {headers: {Accept: 'application/json'}})
            .then((response) => {
                if (response.ok) {
                    return response.json().then((data) => (Array.isArray(data.photos) ? data.photos : []));
                }

                return response.status < 500 ? [] : Promise.reject(response.status);
            })
            .catch(() => {
                lists.delete(src);

                return null;
            }));
    }

    return lists.get(src);
}

function mount(el) {
    if (el.swiper) {
        return;
    }

    const count = el.querySelector('[data-idx-gallery-count]');
    const wrapper = el.querySelector('.swiper-wrapper');
    const first = wrapper && wrapper.querySelector('.swiper-slide');

    if (!first) {
        return;
    }

    // Back to the server's markup: one slide, the cover, no offsets (see COVER).
    const img = first.querySelector('img');

    if (!img) {
        return;
    }

    if (el.hasAttribute(COVER)) {
        img.src = el.getAttribute(COVER);
        img.hidden = false;
        img.setAttribute('loading', 'lazy');
    } else {
        el.setAttribute(COVER, img.getAttribute('src'));
    }

    [...wrapper.children].forEach((slide) => slide !== first && slide.remove());
    wrapper.removeAttribute('style');
    ['style', 'data-swiper-slide-index', 'tabindex'].forEach((name) => first.removeAttribute(name));

    const total = Math.max(parseInt(el.getAttribute('data-idx-gallery-total'), 10) || 1, 1);

    if (count) {
        count.textContent = `1 / ${total}`;
    }

    // The other slides are copies of the cover's: the same link, alt and look.
    const template = first.cloneNode(true);
    template.setAttribute('tabindex', '-1');

    const renderSlide = (slot) => {
        const slide = template.cloneNode(true);
        const photo = slide.querySelector('img');

        // Not yet known: the grey frame. The <img> stays, hidden, so a slide
        // snapshotted by the map can always be turned back into the cover.
        if (slot.url) {
            photo.src = slot.url;
        } else {
            photo.removeAttribute('src');
            photo.hidden = true;
        }

        photo.removeAttribute('loading');

        return slide;
    };

    // The cover is slide 0 (Virtual takes it from the DOM); the rest are slots
    // filled in when the list arrives. Objects, not nulls: appendSlide skips
    // falsy entries.
    const slots = Array.from({length: total - 1}, () => ({url: null}));

    const swiper = new Swiper(el, {
        modules: [Navigation, Pagination, Virtual],
        // The similar-listings block sets these cards in a slider of their own.
        nested: true,
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 300,
        watchOverflow: true,
        virtual: {
            enabled: true,
            renderSlide,
        },
        pagination: {
            el: el.querySelector('.swiper-pagination'),
            // 30–50 photos would not fit as dots: five are shown, sliding along.
            dynamicBullets: true,
            dynamicMainBullets: 3,
        },
        navigation: {
            prevEl: el.querySelector('.idx-gallery__prev'),
            nextEl: el.querySelector('.idx-gallery__next'),
        },
        on: {
            slideChange(s) {
                if (count) {
                    count.textContent = `${s.activeIndex + 1} / ${s.virtual.slides.length}`;
                }
            },
        },
    });

    swiper.virtual.appendSlide(slots);

    // One reach for the gallery is enough: whichever of hover, touch or focus
    // comes first asks for the list, and the other two are dropped with it.
    let listeners = null;

    const fill = () => {
        listeners.abort();

        fetchList(el.getAttribute('data-idx-gallery')).then((photos) => {
            if (swiper.destroyed) {
                return;
            }

            if (!photos) {
                arm();

                return;
            }

            // The page may be older than the list: the feed adds or drops photos.
            // The cover stays put as slide 0, whatever the list says it is.
            const rest = photos.slice(1).map((url) => ({url}));
            const slides = swiper.virtual.slides;

            // The rebuild below takes every slide out of the DOM and puts it back,
            // and a focused link taken out drops the focus to <body>.
            const focused = el.contains(document.activeElement) ? document.activeElement : null;

            slides.splice(1, slides.length - 1, ...rest);
            Object.keys(swiper.virtual.cache).forEach((index) => {
                if (index !== '0') {
                    delete swiper.virtual.cache[index];
                }
            });
            swiper.virtual.update(true);

            if (focused && focused.isConnected) {
                focused.focus({preventScroll: true});
            }

            if (swiper.activeIndex >= slides.length) {
                swiper.slideTo(slides.length - 1, 0);
            }

            if (count) {
                count.textContent = `${swiper.activeIndex + 1} / ${slides.length}`;
            }
        });
    };

    function arm() {
        listeners = new AbortController();

        const {signal} = listeners;

        el.addEventListener('pointerenter', fill, {signal});
        el.addEventListener('touchstart', fill, {signal, passive: true});
        el.addEventListener('focusin', fill, {signal});
    }

    arm();
}

export function initIdxCardGallery() {
    if (!('IntersectionObserver' in window)) {
        return;
    }

    const visible = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                visible.unobserve(entry.target);
                mount(entry.target);
            }
        });
    }, {rootMargin: '200px'});

    const watch = (root) => {
        if (root.matches && root.matches(GALLERY)) {
            visible.observe(root);
        }

        if (root.querySelectorAll) {
            root.querySelectorAll(GALLERY).forEach((el) => visible.observe(el));
        }
    };

    const unmount = (root) => {
        const galleries = root.matches && root.matches(GALLERY) ? [root] : [];

        if (root.querySelectorAll) {
            galleries.push(...root.querySelectorAll(GALLERY));
        }

        galleries.forEach((el) => {
            visible.unobserve(el);

            if (el.swiper) {
                el.swiper.destroy(true, false);
            }
        });
    };

    watch(document);

    new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.removedNodes.forEach((node) => {
                // A node moved within the page is removed and added again.
                if (node.nodeType === Node.ELEMENT_NODE && !node.isConnected) {
                    unmount(node);
                }
            });

            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    watch(node);
                }
            });
        });
    }).observe(document.body, {childList: true, subtree: true});
}
